import './FilterSelect.css'

import { useEffect, useRef, useState } from 'react'

import { cap } from '../../../../State'
import { translations } from '../../../../translations'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Componente Select minimal per i filtri
 * @param {Object} props - Proprietà del componente
 * @param {string|number} props.value - Valore selezionato
 * @param {Function} props.onChange - Funzione chiamata quando cambia la selezione
 * @param {Function} props.onClear - Funzione chiamata per pulire la selezione
 * @param {Array} props.options - Array di opzioni {label, value}
 * @param {string} props.placeholder - Testo placeholder
 * @param {string} props.id - ID univoco per il componente
 * @param {string} props.optionLabel - Nome della proprietà per il label (default: 'label')
 * @param {string} props.optionValue - Nome della proprietà per il value (default: 'value')
 * @returns {JSX.Element} Componente Select
 */
export function FilterSelect({
  value,
  onChange,
  onClear,
  options = [],
  placeholder = t.gfSeleziona,
  id,
  optionLabel = 'label',
  optionValue = 'value'
}) {
  const [isOpen, setIsOpen] = useState(false)
  const selectRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const getOptionValue = (opt) => {
    return typeof opt === 'object' ? opt[optionValue] : opt
  }

  const getOptionLabel = (opt) => {
    return typeof opt === 'object' ? opt[optionLabel] : opt
  }

  const selectedOption = options.find((opt) => {
    return getOptionValue(opt) === value
  })

  const selectedLabel = selectedOption ? getOptionLabel(selectedOption) : null

  const handleSelect = (option) => {
    const optValue = getOptionValue(option)
    setIsOpen(false)
    onChange && onChange(optValue)
  }

  const hasValue = value !== null && value !== undefined && value !== ''

  return (
    <div className="gigi-filter-select" ref={selectRef}>
      <div className="gigi-filter-select-input" onClick={() => setIsOpen(!isOpen)} id={id}>
        <span
          className={selectedLabel ? 'gigi-filter-select-value' : 'gigi-filter-select-placeholder'}
        >
          {selectedLabel || placeholder}
        </span>
        <div className="gigi-filter-select-actions">
          {hasValue && onClear && (
            <button
              type="button"
              className="gigi-filter-select-clear"
              onClick={(e) => {
                e.stopPropagation()
                onClear()
              }}
              title={t.ggPulisci}
            >
              <i className="pi pi-times" />
            </button>
          )}
          <i
            className={`pi pi-chevron-down gigi-filter-select-icon ${isOpen ? 'gigi-filter-select-icon-open' : ''}`}
          />
        </div>
      </div>

      {isOpen && (
        <div className="gigi-filter-select-dropdown">
          {options.length === 0 ? (
            <div className="gigi-filter-select-option-empty">{t.ggNessunaOpzione}</div>
          ) : (
            options.map((option, index) => {
              const optValue = getOptionValue(option)
              const optLabel = getOptionLabel(option)
              const isSelected = optValue === value

              return (
                <div
                  key={index}
                  className={`gigi-filter-select-option ${isSelected ? 'gigi-filter-select-option-selected' : ''}`}
                  onClick={() => handleSelect(option)}
                >
                  {optLabel}
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
